/**
 * Recebimento de produtos (ADR 0046, Entrega 2), usado pela API e pelas telas: protocolo da
 * portaria, recebimento físico por item, conferência e aceite técnico, divergências e saldo.
 * O protocolo não é recebimento nem aceite; o recebimento físico não é aceite. Nada aqui muda preço,
 * quantidade contratada ou especificação do pedido, nem é aprovação, alçada ou pagamento.
 * Entrega 3: aceite de serviços, com a execução informada por quem compra ou pelo responsável
 * técnico, pela modalidade validada (conclusão, etapas ou períodos), nunca acima do contratado.
 * Entrega 4: cancelamento de saldo (Compras pede, o gestor do centro de custo confirma, Compras
 * executa) e estornos de recebimento, conferência e cancelamento, com reabertura. Fora: lembretes (5).
 */
import { z } from 'zod';
import { decimalString, uuid } from './schemas.ts';

/** Excedente, avaria, item errado ou especificação divergente. */
export const DIVERGENCE_KINDS = ['excess', 'damaged', 'wrong_item'] as const;
export type DivergenceKind = (typeof DIVERGENCE_KINDS)[number];

/** Recusa na conferência de produto: só avaria ou item errado (excedente é visto na chegada). */
export const REJECTION_KINDS = ['damaged', 'wrong_item'] as const;

/** Recusa na conferência de serviço: execução não conforme. */
export const SERVICE_REJECTION_KINDS = ['nonconforming'] as const;

/**
 * open: pendente; returned: devolução registrada; new_purchase: excedente virou outra compra;
 * reversed: deixou de valer com o estorno do registro de origem.
 */
export const DIVERGENCE_STATUSES = ['open', 'returned', 'new_purchase', 'reversed'] as const;
export type DivergenceStatus = (typeof DIVERGENCE_STATUSES)[number];

/** Tipos de documento da evidência (sem validação tributária). */
export const RECEIVING_DOCUMENT_KINDS = [
  'invoice',
  'delivery_proof',
  'gate_protocol',
  'photo',
  'report',
  'other',
] as const;
export type ReceivingDocumentKind = (typeof RECEIVING_DOCUMENT_KINDS)[number];

/** A que registro a evidência se liga. */
export const RECEIVING_ATTACHMENT_TARGETS = [
  'gate_protocol',
  'receipt',
  'acceptance',
  'divergence',
] as const;
export type ReceivingAttachmentTarget = (typeof RECEIVING_ATTACHMENT_TARGETS)[number];

/**
 * Cancelamento de saldo: requested (aguardando o gestor do centro de custo), blocked (impacto
 * declarado: não é executado), confirmed (aguardando Compras executar), rejected, withdrawn,
 * executed (saldo cancelado) e reversed (execução estornada).
 */
export const CANCELLATION_STATUSES = [
  'requested',
  'blocked',
  'confirmed',
  'rejected',
  'withdrawn',
  'executed',
  'reversed',
] as const;
export type CancellationStatus = (typeof CANCELLATION_STATUSES)[number];

/** O que pode ser estornado. */
export const REVERSAL_TARGETS = ['receipt', 'acceptance', 'cancellation'] as const;
export type ReversalTarget = (typeof REVERSAL_TARGETS)[number];

/** Saldo de um item do pedido, como o quadro do recebimento devolve (texto com 4 casas). */
export interface DeliveryItemBalance {
  kind: 'product' | 'service';
  contracted: string;
  /** Cancelada por cancelamento de saldo executado (e não estornado). */
  cancelled: string;
  /** Ainda pode ser cancelada: produto, o que não chegou em boas condições; serviço, o não aceito. */
  cancelable: string;
  /** Recebida em boas condições (inclui o que depois foi recusado na conferência). */
  received: string;
  accepted: string;
  /** Recusada na conferência. */
  rejected: string;
  /** Ainda pode chegar: contratada − cancelada − (recebida − recusada). */
  receivable: string;
  /** Recebida e ainda sem conferência. */
  awaiting: string;
  /** Contratada − cancelada − aceita: o que falta para concluir. */
  pending: string;
  openDivergence: string;
}

const positive = (v: string) => Number(v) > 0;

/** O que a pessoa pode fazer no recebimento desta compra agora. As telas mostram só isso. */
export function deliveryActions(input: {
  requestStatus: string;
  hasActiveOrder: boolean;
  /** Recebe ou compra na empresa da compra. */
  canReceive: boolean;
  /** Compra na empresa da compra (resolve divergências). */
  buyer: boolean;
  /** Informa a execução de serviço: quem compra ou o responsável técnico atual (N11). */
  canInformExecution?: boolean;
  /** É o responsável técnico atual e não está impedido. */
  canAccept: boolean;
  items: readonly DeliveryItemBalance[];
  openDivergences: number;
  /** Há um cancelamento de saldo em andamento (pedido, bloqueado ou confirmado) neste pedido. */
  cancellationInProgress?: boolean;
}) {
  const open =
    input.hasActiveOrder && ['ordered', 'partially_delivered'].includes(input.requestStatus);
  const products = input.items.filter((i) => i.kind === 'product');
  return {
    /** Registrar o protocolo da portaria (a chegada, sem conferir itens). */
    recordGateProtocol: open && input.canReceive,
    /** Registrar o recebimento físico (inclusive o excedente, como divergência). */
    recordReceipt: open && input.canReceive && products.length > 0,
    /** Informar a execução de serviço com saldo em aberto (Entrega 3). */
    informExecution:
      open &&
      !!input.canInformExecution &&
      input.items.some((i) => i.kind === 'service' && positive(i.receivable)),
    /** Conferir e aceitar ou recusar o que chegou ou foi executado. */
    accept: open && input.canAccept && input.items.some((i) => positive(i.awaiting)),
    /** Registrar a devolução ou a regularização de uma divergência. */
    resolveDivergence: input.buyer && input.openDivergences > 0,
    /** Pedir o cancelamento de saldo (um por vez no pedido). */
    requestCancellation:
      open &&
      input.buyer &&
      !input.cancellationInProgress &&
      input.items.some((i) => positive(i.cancelable)),
  };
}
export type DeliveryActions = ReturnType<typeof deliveryActions>;

// ----- Entradas -----

const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();

const quantity = decimalString(10, 4);
const positiveQuantity = quantity.refine(positive, 'A quantidade precisa ser maior que zero');

/**
 * Identificador da operação, gerado pela tela uma vez por envio: o mesmo envio repetido (duplo
 * clique, reenvio depois de falha de rede) é recusado em vez de gravar duas vezes.
 */
const operationId = uuid;

export const gateProtocolInput = z.object({
  operationId,
  arrivedAt: z.iso.datetime({ offset: true }),
  reference: optionalText(100),
  notes: optionalText(1000),
});
export type GateProtocolInput = z.infer<typeof gateProtocolInput>;

export const receiptDivergenceInput = z.object({
  kind: z.enum(DIVERGENCE_KINDS),
  quantity: positiveQuantity,
  description: z.string().trim().min(5, 'Descreva o que aconteceu').max(2000),
});

export const receiptLineInput = z.object({
  orderItemId: uuid,
  /** Recebida em boas condições (zero quando tudo o que chegou deste item é divergência). */
  quantity,
  notes: optionalText(1000),
  divergences: z.array(receiptDivergenceInput).max(5).default([]),
});

export const receiptInput = z
  .object({
    operationId,
    arrivedOn: z.iso.date(),
    gateProtocolId: uuid.nullable().optional(),
    notes: optionalText(2000),
    lines: z.array(receiptLineInput).min(1).max(200),
  })
  .superRefine((v, ctx) => {
    const ids = new Set<string>();
    for (const [index, line] of v.lines.entries()) {
      if (ids.has(line.orderItemId)) {
        ctx.addIssue({ code: 'custom', path: ['lines', index], message: 'Item repetido' });
      }
      ids.add(line.orderItemId);
    }
    if (!v.lines.some((l) => positive(l.quantity) || l.divergences.length > 0)) {
      ctx.addIssue({
        code: 'custom',
        path: ['lines'],
        message: 'Informe ao menos uma quantidade recebida ou uma divergência',
      });
    }
  });
export type ReceiptInput = z.infer<typeof receiptInput>;

export const acceptanceLineInput = z.object({
  receiptLineId: uuid,
  /** Aceita depois da conferência técnica. */
  accepted: quantity.default('0'),
  /**
   * Recusada, com a descrição (vira divergência): avaria ou item errado no produto, execução não
   * conforme no serviço (a API confere o tipo do item).
   */
  rejection: z
    .object({
      kind: z.enum([...REJECTION_KINDS, ...SERVICE_REJECTION_KINDS]),
      quantity: positiveQuantity,
      description: z.string().trim().min(5, 'Descreva o que foi encontrado').max(2000),
    })
    .nullable()
    .optional(),
});

export const acceptanceInput = z
  .object({
    operationId,
    notes: optionalText(2000),
    lines: z.array(acceptanceLineInput).min(1).max(200),
  })
  .superRefine((v, ctx) => {
    if (!v.lines.some((l) => positive(l.accepted) || l.rejection)) {
      ctx.addIssue({
        code: 'custom',
        path: ['lines'],
        message: 'Aceite ou recuse ao menos uma quantidade',
      });
    }
  });
export type AcceptanceInput = z.infer<typeof acceptanceInput>;

export const divergenceResolveInput = z
  .object({
    outcome: z.enum(['returned', 'new_purchase']),
    note: z.string().trim().min(5, 'Explique o que foi feito').max(2000),
    /** Excedente regularizado: o número da outra compra (SC), da mesma empresa. */
    newRequestNumber: z.coerce.number().int().positive().nullable().optional(),
  })
  .superRefine((v, ctx) => {
    if ((v.outcome === 'new_purchase') !== !!v.newRequestNumber) {
      ctx.addIssue({
        code: 'custom',
        path: ['newRequestNumber'],
        message: 'Informe a outra compra só na regularização por nova compra',
      });
    }
  });
export type DivergenceResolveInput = z.infer<typeof divergenceResolveInput>;

// ----- Entrega 4: cancelamento de saldo e estornos -----

export const cancellationLineInput = z.object({
  orderItemId: uuid,
  quantity: positiveQuantity,
});

export const balanceCancellationInput = z
  .object({
    operationId,
    reason: z.string().trim().min(10, 'Explique o motivo (ao menos 10 letras)').max(2000),
    /** Multa, custo ou outro impacto contratual ou financeiro (N2): com impacto, não é executado. */
    impactDeclared: z.boolean(),
    impactDescription: optionalText(2000),
    lines: z.array(cancellationLineInput).min(1).max(200),
  })
  .superRefine((v, ctx) => {
    const ids = new Set<string>();
    for (const [index, line] of v.lines.entries()) {
      if (ids.has(line.orderItemId)) {
        ctx.addIssue({ code: 'custom', path: ['lines', index], message: 'Item repetido' });
      }
      ids.add(line.orderItemId);
    }
    if (v.impactDeclared && (!v.impactDescription || v.impactDescription.length < 5)) {
      ctx.addIssue({
        code: 'custom',
        path: ['impactDescription'],
        message: 'Descreva a multa, o custo ou o impacto',
      });
    }
    if (!v.impactDeclared && v.impactDescription) {
      ctx.addIssue({
        code: 'custom',
        path: ['impactDescription'],
        message: 'Descreva o impacto só quando houver',
      });
    }
  });
export type BalanceCancellationInput = z.infer<typeof balanceCancellationInput>;

export const cancellationDecisionInput = z
  .object({
    decision: z.enum(['confirm', 'reject']),
    note: optionalText(2000),
  })
  .superRefine((v, ctx) => {
    if (v.decision === 'reject' && (!v.note || v.note.length < 5)) {
      ctx.addIssue({ code: 'custom', path: ['note'], message: 'Explique por que não confirma' });
    }
    if (v.note && v.note.length < 5) {
      ctx.addIssue({ code: 'custom', path: ['note'], message: 'Escreva ao menos 5 letras' });
    }
  });
export type CancellationDecisionInput = z.infer<typeof cancellationDecisionInput>;

export const cancellationWithdrawInput = z.object({
  note: z.string().trim().min(5, 'Explique por que desiste').max(2000),
});

export const reversalInput = z.object({
  operationId,
  target: z.enum(REVERSAL_TARGETS),
  targetId: uuid,
  reason: z.string().trim().min(10, 'Explique o motivo do estorno (ao menos 10 letras)').max(2000),
});
export type ReversalInput = z.infer<typeof reversalInput>;

// ----- Entrega 3: execução de serviço -----

export const executionLineInput = z
  .object({
    orderItemId: uuid,
    quantity: positiveQuantity,
    /** Etapa (modalidade "etapas"), na ordem validada. */
    milestoneNo: z.coerce.number().int().min(1).max(200).nullable().optional(),
    /** Início do período (modalidade "períodos"). */
    periodStart: z.iso.date().nullable().optional(),
    /** OS do Facilities usada como evidência (só a referência). */
    workOrderCode: z
      .string()
      .trim()
      .regex(/^OS-[0-9]{4}-[0-9]{6}$/, 'Use o número da OS, como OS-2026-000123')
      .nullable()
      .optional()
      .or(z.literal('').transform(() => null)),
  })
  .refine((v) => !(v.milestoneNo && v.periodStart), {
    path: ['periodStart'],
    message: 'Informe a etapa ou o período, não os dois',
  });

export const executionInput = z
  .object({
    operationId,
    /** Data em que o serviço (ou a etapa, ou o período) foi executado. */
    executedOn: z.iso.date(),
    notes: optionalText(2000),
    lines: z.array(executionLineInput).min(1).max(200),
  })
  .superRefine((v, ctx) => {
    const keys = new Set<string>();
    for (const [index, line] of v.lines.entries()) {
      const key = `${line.orderItemId}:${line.milestoneNo ?? ''}:${line.periodStart ?? ''}`;
      if (keys.has(key)) {
        ctx.addIssue({ code: 'custom', path: ['lines', index], message: 'Item repetido' });
      }
      keys.add(key);
    }
  });
export type ExecutionInput = z.infer<typeof executionInput>;
